var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@apollo/client';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import GET_ORDER_PENDING_APPROVAL_DELIVERIES from 'api/queries/GET_ORDER_PENDING_APPROVAL_DELIVERIES';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import InfoBox from 'layout/atoms/InfoBox/InfoBox';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { useAppDispatch } from 'store/index';
import { selectModalByName } from 'store/layoutSlice';
import { approveOrderComponentVariantDeliveries, rejectOrderComponentVariantDeliveries, selectOrderDetailsRefetchFlags, } from 'store/orderDetailsSlice';
/** Red "Reject" button that only fires after a small inline confirmation popover. */
function ConfirmRejectButton({ label, onConfirm, loading, disabled }) {
    const { t } = useTranslation(['orders']);
    return (React.createElement(Dropdown, { position: "topRight", trigger: React.createElement(Button, { color: "danger", loading: loading, disabled: disabled }, label) }, (close) => (React.createElement("div", { style: { padding: '12px', width: '220px' } },
        React.createElement(Grid, { columns: 1, gap: 12 },
            React.createElement("div", { style: { fontSize: '13px' } }, t('delivery_approval_list_reject_confirm')),
            React.createElement(Grid, { columns: 2, gap: 8 },
                React.createElement(Button, { theme: "border", onClick: close }, t('delivery_approval_list_reject_cancel')),
                React.createElement(Button, { color: "danger", onClick: () => {
                        close();
                        onConfirm();
                    } }, t('delivery_approval_list_reject'))))))));
}
function DeliveryApprovalList() {
    var _a, _b;
    const { t } = useTranslation(['orders']);
    const dispatch = useAppDispatch();
    const modalData = (_a = useSelector(selectModalByName('deliveryApprovalList'))) === null || _a === void 0 ? void 0 : _a.data;
    const orderId = modalData === null || modalData === void 0 ? void 0 : modalData.orderId;
    const deliveriesChangedFlag = useSelector(selectOrderDetailsRefetchFlags).fetchOrderVolume;
    const [pendingIds, setPendingIds] = useState(new Set());
    const [errorMessage, setErrorMessage] = useState('');
    const { data, loading, refetch } = useQuery(GET_ORDER_PENDING_APPROVAL_DELIVERIES, {
        variables: { id: orderId },
        skip: !orderId,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });
    useEffect(() => {
        if (deliveriesChangedFlag && orderId) {
            refetch({ id: orderId });
        }
    }, [deliveriesChangedFlag, orderId, refetch]);
    const deliveries = ((_b = data === null || data === void 0 ? void 0 : data.node) === null || _b === void 0 ? void 0 : _b.pendingApprovalDeliveries) || [];
    const runAction = (thunk, deliveryIds, failedMessageKey) => __awaiter(this, void 0, void 0, function* () {
        if (!orderId || deliveryIds.length === 0)
            return;
        setErrorMessage('');
        setPendingIds((prev) => new Set([...prev, ...deliveryIds]));
        try {
            yield dispatch(thunk({ orderId, deliveryIds })).unwrap();
            yield refetch({ id: orderId });
        }
        catch (error) {
            setErrorMessage((error === null || error === void 0 ? void 0 : error.message) || t(failedMessageKey));
        }
        finally {
            setPendingIds((prev) => {
                const next = new Set(prev);
                deliveryIds.forEach((id) => next.delete(id));
                return next;
            });
        }
    });
    const approve = (deliveryIds) => runAction(approveOrderComponentVariantDeliveries, deliveryIds, 'delivery_approval_list_approve_failed');
    const reject = (deliveryIds) => runAction(rejectOrderComponentVariantDeliveries, deliveryIds, 'delivery_approval_list_reject_failed');
    const handleApproveAll = () => approve(deliveries.map((delivery) => delivery.id));
    const handleRejectAll = () => reject(deliveries.map((delivery) => delivery.id));
    return (React.createElement(Modal, { alwaysScroll: true, name: "deliveryApprovalList", size: "m", header: React.createElement(Label, { weight: "strong" }, t('delivery_approval_list')) },
        React.createElement(Grid, { columns: 1, gap: 16 },
            loading && !data && React.createElement(Loader, null),
            !loading && deliveries.length === 0 && (React.createElement(EmptyState, { noHeader: true, subheader: t('delivery_approval_list_empty') })),
            errorMessage && React.createElement(InfoBox, { type: "alert", text: errorMessage, noBorder: true }),
            deliveries.length > 0 && (React.createElement("div", { style: { display: 'flex', gap: '8px' } },
                React.createElement(Button, { theme: "border", onClick: handleApproveAll, loading: pendingIds.size === deliveries.length && pendingIds.size > 0, disabled: pendingIds.size > 0 }, t('delivery_approval_list_approve_all')),
                React.createElement(ConfirmRejectButton, { label: t('delivery_approval_list_reject_all'), onConfirm: handleRejectAll, loading: pendingIds.size === deliveries.length && pendingIds.size > 0, disabled: pendingIds.size > 0 }))),
            deliveries.map((delivery) => {
                var _a, _b;
                return (React.createElement("div", { key: delivery.id, style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        border: '1px solid var(--static-stroke-subtle)',
                        borderRadius: '6px',
                        padding: '12px',
                    } },
                    React.createElement("div", null,
                        React.createElement("div", { style: { fontWeight: 600 } },
                            ((_a = delivery.orderComponentVariant) === null || _a === void 0 ? void 0 : _a.name) || '-',
                            ((_b = delivery.orderComponentVariant) === null || _b === void 0 ? void 0 : _b.color)
                                ? ` (${delivery.orderComponentVariant.color})`
                                : ''),
                        React.createElement("div", { style: { fontSize: '13px', color: 'var(--static-content-subtle)' } },
                            t('delivery_approval_list_planned_date'),
                            ":",
                            ' ',
                            delivery.plannedDate ? moment(delivery.plannedDate).format('YYYY-MM-DD') : '-',
                            ' — ',
                            t('delivery_approval_list_quantity'),
                            ": ",
                            delivery.quantityPlanned)),
                    React.createElement("div", { style: { display: 'flex', gap: '8px' } },
                        React.createElement(Button, { color: "primary", onClick: () => approve([delivery.id]), loading: pendingIds.has(delivery.id), disabled: pendingIds.size > 0 && !pendingIds.has(delivery.id) }, t('delivery_approval_list_approve')),
                        React.createElement(ConfirmRejectButton, { label: t('delivery_approval_list_reject'), onConfirm: () => reject([delivery.id]), loading: pendingIds.has(delivery.id), disabled: pendingIds.size > 0 && !pendingIds.has(delivery.id) }))));
            }))));
}
export default DeliveryApprovalList;
